import type { ContentDocument } from "../../API/Models";

/**
 * Inflation row labels : order matters; mirrors the C# deploy projector
 * (`InflationRowMapping` in `ContentService.DynamicComponents.Inflation.cs`).
 * Keep this list and the C# one in sync.
 */
export const INFLATION_ROW_MAPPING: ReadonlyArray<{ readonly fieldKey: string; readonly label: string }> = [
	{ fieldKey: "glissement-annuel", label: "Glissement annuel" },
	{ fieldKey: "hebdomadaire", label: "Hebdomadaire" },
	{ fieldKey: "cumule-annee", label: "Cumulé de l'année" }
];

export type InflationProjectionInput = {
	/** All loaded rows for the configured content type. The function picks the
	 * latest by `sortField` (or `updatedAt` fallback) and projects it. */
	readonly rows: ReadonlyArray<ContentDocument>;
	/** Sort field (e.g. "date"). Falls back to `updatedAt` when empty. */
	readonly sortField?: string | null;
	/** Optional per-key overrides : empty/missing entries fall back to the
	 * default key. Mirrors the deploy projector's optional fieldMapping. */
	readonly fieldMapping?: Record<string, string | null | undefined> | null;
	/** Numeric field whose last N values plot the sparkline. Empty → no sparkline. */
	readonly sparklineField?: string | null;
	/** Window size for the sparkline. Default 8. */
	readonly sparklineWindow?: number | null;
};

export type InflationProjectionResult = {
	readonly asOf: string | null;
	readonly source: string | null;
	readonly rows: ReadonlyArray<{ readonly label: string; readonly value: string }>;
	/** Oldest → newest for left-to-right charting. Null when not configured
	 * or fewer than 2 valid values are available. */
	readonly sparkline: ReadonlyArray<number> | null;
};

const trim = (v: unknown): string => (v == null ? "" : String(v).trim());

/**
 * Re-formats an ISO date (yyyy-MM-dd) to the DD/MM/YYYY display form used in
 * the home card, matching the C# `FormatInflationDate` helper. Passes through
 * any other shape unchanged.
 */
function formatInflationDate(raw: string): string {
	const v = trim(raw);
	if (!v) return v;
	const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
	if (isoMatch) {
		return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
	}
	return v;
}

function formatInflationNumber(raw: string): string {
	const v = trim(raw);
	if (!v) return v;
	if (v.includes(",")) return v;
	const n = Number(v);
	if (!Number.isFinite(n)) return v;
	// Match the C# "0.###" + fr-FR locale : comma decimal, up to 3 fractional digits.
	const parts = n.toFixed(3).replace(/0+$/, "").replace(/\.$/, "").split(".");
	return parts.length === 1 ? parts[0] : `${parts[0]},${parts[1]}`;
}

/**
 * Sort all rows newest-first by the given field (with `updatedAt` fallback
 * when the field is missing). Returns a NEW array : never mutates `rows`.
 */
function sortByRecency(
	rows: ReadonlyArray<ContentDocument>,
	sortField: string
): ContentDocument[] {
	const dateOf = (doc: ContentDocument): number => {
		const raw = sortField
			? (doc.data as Record<string, unknown> | undefined)?.[sortField]
			: undefined;
		const candidate = raw ?? doc.updatedAt;
		if (!candidate) return 0;
		const t = new Date(String(candidate)).getTime();
		return Number.isFinite(t) ? t : 0;
	};
	return [...rows].sort((a, b) => dateOf(b) - dateOf(a));
}

function parseFrenchOrEnglishNumber(raw: unknown): number | null {
	if (raw == null) return null;
	const s = String(raw).trim();
	if (!s) return null;
	const normalised = s.includes(",") && !s.includes(".") ? s.replace(",", ".") : s;
	const n = Number(normalised);
	return Number.isFinite(n) ? n : null;
}

/**
 * Pure projection: given the loaded rows for a content type and the dynamic
 * config, returns the asOf / source / rows the inflation card should render.
 * Returns null when no row is available : caller decides how to fall back.
 *
 * The algorithm intentionally mirrors `ContentService.ApplyDynamicMarketsInflationAsync`
 * so the admin preview matches what the deploy will produce. Tests live in
 * `projectMarketsInflation.test.ts`.
 */
export function projectMarketsInflation(input: InflationProjectionInput): InflationProjectionResult | null {
	const sortField = trim(input.sortField) || "date";
	const sorted = sortByRecency(input.rows, sortField);
	const latest = sorted[0] ?? null;
	if (!latest) return null;
	const data = (latest.data ?? {}) as Record<string, unknown>;
	const map = input.fieldMapping ?? {};
	const resolveKey = (defaultKey: string): string => trim(map[defaultKey]) || defaultKey;

	const dateRaw = trim(data[resolveKey("date")]);
	const sourceRaw = trim(data[resolveKey("source")]);

	const rows: { label: string; value: string }[] = [];
	for (const { fieldKey, label } of INFLATION_ROW_MAPPING) {
		const resolvedKey = resolveKey(fieldKey);
		const raw = data[resolvedKey];
		if (raw == null || raw === "") continue;
		rows.push({ label, value: formatInflationNumber(String(raw)) });
	}

	// Sparkline projection : opt-in via `sparklineField`. Mirrors the C#
	// projector: take the last `sparklineWindow` rows (newest-first), parse
	// the configured numeric field, drop unparseable rows, then reverse so
	// the chart plots oldest→newest left-to-right.
	const sparklineFieldRaw = trim(input.sparklineField);
	let sparkline: number[] | null = null;
	if (sparklineFieldRaw) {
		const sparklineKey = resolveKey(sparklineFieldRaw);
		const window =
			input.sparklineWindow != null && Number.isFinite(input.sparklineWindow) && input.sparklineWindow > 0
				? Math.floor(input.sparklineWindow)
				: 8;
		const slice = sorted.slice(0, window);
		const points: number[] = [];
		for (const doc of slice) {
			const d = (doc.data ?? {}) as Record<string, unknown>;
			const n = parseFrenchOrEnglishNumber(d[sparklineKey]);
			if (n != null) points.push(n);
		}
		if (points.length >= 2) {
			points.reverse();
			sparkline = points;
		}
	}

	return {
		asOf: dateRaw ? `Date : ${formatInflationDate(dateRaw)}` : null,
		source: sourceRaw || null,
		rows,
		sparkline
	};
}
